<template>
    <div :id="id">
        <multiselect
            :dusk="id"
            v-bind="mergedOptions"
            :value="value"
            :block-keys="['Delete']"
            :class="{ 'is-invalid': error }"
            :disabled="disabled"
            :group-values="groupValues"
            :group-label="groupLabel"
            :group-select="groupSelect"
            :clearOnSelect="true"
            @open="handleOpen"
            @close="isOpen = false"
            @tag="$emit('tag', $event)"
            @input="$emit('input', $event)"
            @remove="$emit('remove', $event)"
            @select="$emit('select', $event)"
            @search-change="$emit('on-search', $event)"
            ref="multiselect"
        >
            <!--       Custom Arrows-->
            <span
                slot="caret"
                slot-scope="{ toggle }"
                class="m-arrow m-arrow-down"
                v-if="!value"
                @mousedown.prevent.stop="toggle"
            >
                <img src="/imgs/icons/arrow_down.svg" alt="" v-if="isOpen" />
                <img src="/imgs/icons/arrow_up.svg" alt="" v-else />
            </span>
            <span
                slot="caret"
                slot-scope="{ toggle }"
                class="m-arrow m-arrow-up"
                v-else
                @mousedown.prevent.stop="toggle"
            >
                <img src="/imgs/icons/arrow_down.svg" alt="" v-if="isOpen" />
                <img src="/imgs/icons/arrow_up.svg" alt="" v-else />
            </span>
            <!--        End custom arrows-->
            <template #singleLabel="{option}">
                <slot name="singleOption" :option="option">
                    {{ mergedOptions.label ? option[mergedOptions.label] : option }}
                </slot>
            </template>
            <template #option="{ option }">
                <slot name="option" :option="option">
                    <input
                        class="form__checkbox"
                        type="checkbox"
                        v-model="option.checked"
                        @focus.prevent
                        v-if="mergedOptions.multiple"
                    />
                    <label v-if="option.$groupLabel">{{ option.$groupLabel }}</label>
                    <label v-else>{{ mergedOptions.label ? option[mergedOptions.label] : option }}</label>
                </slot>
            </template>
            <template #noOptions>
                {{ $t("multiselect.noOptions") }}
            </template>
            <template #noResult>
                {{ $t("multiselect.noResult") }}
            </template>
        </multiselect>
    </div>
</template>

<script>
import Multiselect from "vue-multiselect";

export default {
    name: "VMultiselect",
    components: {
        Multiselect
    },
    props: {
        options: {
            type: Object,
            default: {}
        },
        disabled: {
            type: Boolean,
            default: false
        },
        id: { type: String },
        error: { type: [String, Boolean] },
        value: {},
        groupValues: {
            type: String
        },
        groupLabel: {
            type: String
        },
        groupSelect: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            defaultOptions: {
                multiple: true,
                taggable: false,
                showLabels: false,
                closeOnSelect: true,
                clearOnSelect: false,
                preserveSearch: true,
                preselectFirst: false,
                placeholder: this.$t("multiselect.placeholder"),
                tagPlaceholder: this.$t("multiselect.tagPlaceholder"),
                selectLabel: this.$t("multiselect.selectLabel"),
                selectGroupLabel: this.$t("multiselect.selectGroupLabel"),
                selectedLabel: this.$t("multiselect.selectedLabel"),
                deselectLabel: this.$t("multiselect.deselectLabel"),
                deselectGroupLabel: this.$t("multiselect.deselectGroupLabel"),
                limitText: number => `... +${number}`
            },
            isOpen: false
        };
    },
    computed: {
        mergedOptions() {
            return Object.assign(this.defaultOptions, this.options);
        },
        multiselect() {
            return this.$refs.multiselect;
        }
    },
    methods: {
        handleOpen(event) {
            this.$emit("open", event);
            this.isOpen = true;
        }
    }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
.is-invalid > .multiselect__tags {
    border: 1.5px solid #cf5f5f !important;
    color: #cf5f5f;

    > .multiselect__placeholder {
        color: #cf5f5f;
    }
}

.multiselect {
    border-radius: 8px;

    label {
        cursor: pointer;
    }

    &.is-invalid {
        border: none !important;
    }

    &__tags {
        border-color: #e0dee3;
        border-radius: 8px;
    }

    &__tag {
        background-color: #6b5fcf;

        &-icon {

            &:hover,
            &:focus {
                background: #5636A2;

                &::after {
                    color: #fff;
                }
            }

            &::after {
                color: #b7b7df;
            }
        }
    }

    &__placeholder {
        font-size: 16px;
        line-height: 1.19;
        font-weight: 500;
        font-family: "Gilroy", sans-serif;
        color: #e0dee3;
    }

    &--active {
        > .multiselect__tags {
            border: 1.5px solid #6b5fcf;
            border-bottom: none;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    &--above {
        > .multiselect__tags {
            border: 1.5px solid #6b5fcf;
            border-bottom: 1.5px solid #6b5fcf !important;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
        }

        .multiselect__content-wrapper {
            border: 1.5px solid #6b5fcf;
            border-top: 1.5px solid #6b5fcf !important;
            border-bottom: none;
            margin-bottom: -2px;
            border-top-left-radius: 8px !important;
            border-top-right-radius: 8px !important;
        }
    }

    &__content-wrapper {
        border: 1.5px solid #6b5fcf;
        border-top: none;
        padding: 8px;
        margin-top: -2px;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    &__spinner {
        position: absolute;
        right: 6.5px;
        top: 4.5px;
        width: 30px;
        height: 30px;
        background: #fff;
        display: block;

        &:before,
        &:after {
            border-top-color: #776ebf !important;
        }
    }

    &__input {
        line-height: 1.19;
        color: #767575;

        &::placeholder {
            color: #e0dee3;
        }
    }

    &__option {
        color: #646464;
        font-family: "Gilroy", sans-serif;
        font-weight: 500;
        font-size: 16px;
        line-height: 1.19;
        height: 40px;
        display: flex;
        align-items: center;
        margin: 8px 0;

        &--highlight {
            background: #f5f4f8 !important;
            border-radius: 16px;
            transition: 0.2s all ease-in;

            &:after {
                background: #b2b6bc !important;
                border-bottom-right-radius: 16px;
                border-top-right-radius: 16px;
            }
        }

        &--selected {
            border-radius: 16px;
            transition: 0.2s all ease-in;
            background: #6a5fcf !important;
            color: white;

            label {
                color: white;
            }

            &:hover {
                background: #f5f4f8 !important;
                color: #646464;
                cursor: pointer;

                label {
                    color: #646464;
                }
            }
        }
    }

    &__option--group {
        pointer-events: none;
    }

    &__single {
        color: #646464;
        font-size: 16px;
        font-family: "Gilroy", sans-serif;
        font-weight: 500;
    }
}

.m-arrow {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);

    img {
        height: 25px;
        width: 25px;
        transition: 0.2s all ease-out;
    }

    &-up img {
        transform: rotate(180deg);
    }
}

.is-invalid {
    &.multiselect {
        .multiselect__content-wrapper {
            border: 1.5px solid #cf5f5f !important;
            border-top: none !important;
        }

        &--above {
            > .multiselect__tags,
            multiselect__content-wrapper {
                border-bottom: 1.5px solid #cf5f5f !important;
            }

            .multiselect__content-wrapper {
                border-bottom: none !important;
            }
        }
    }
}
</style>
